import React, { FC, Fragment } from 'react';
import styled, { css } from 'styled-components';
import { SkeletonBlock } from '../skeleton';

export type DropdownItemIconSize = 'small' | 'large';

export interface DropdownSkeletonItemConfig {
    textWidth?: string;
    captionWidth?: string;
    icon?: boolean;
}

export interface DropdownSkeletonProps {
    itemCount?: number;
    sections?: DropdownSkeletonItemConfig[][];
    iconSize?: DropdownItemIconSize;
    footerWidths?: string[];
    footerJustify?: string;
}

const dropdownCardStyle = css`
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`;

export const DropdownSurface = styled.div<{
    x: number;
    y: number;
    $visible: boolean;
    $zIndex?: number;
    $width?: string;
    $minWidth?: string;
    $maxWidth?: string;
    $maxHeight?: string;
    $padding?: string;
    $transparent?: boolean;
}>`
    position: fixed;
    left: ${(props) => props.x}px;
    top: ${(props) => props.y}px;
    width: ${(props) => props.$width ?? 'auto'};
    min-width: ${(props) => props.$minWidth ?? 'auto'};
    max-width: ${(props) => props.$maxWidth ?? 'calc(100vw - 16px)'};
    max-height: ${(props) => props.$maxHeight ?? 'calc(100vh - 16px)'};
    padding: ${(props) => (props.$transparent ? '0' : (props.$padding ?? '6px'))};
    height: auto;
    z-index: ${(props) => props.$zIndex ?? 100};
    visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    ${(props) => !props.$transparent && dropdownCardStyle}
`;

// Standalone card rendered above the dropdown panel, used for a primary action that must stand apart from the list.
export const DropdownDetachedTop = styled.div`
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    margin-bottom: 6px;
    padding: 6px;
    overflow: hidden;

    ${dropdownCardStyle}
`;

// Panel holding the scrollable dropdown content when a detached card is rendered above it.
export const DropdownPanel = styled.div<{ $padding?: string }>`
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: ${(props) => props.$padding ?? '0'};
    overflow: hidden;

    ${dropdownCardStyle}
`;

export const DropdownOverlay = styled.div<{ $zIndex?: number; $background?: string }>`
    position: fixed;
    inset: 0;
    background: ${(props) => props.$background ?? 'rgba(0, 0, 0, 0.35)'};
    z-index: ${(props) => props.$zIndex ?? 99};
`;

export const DropdownHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
`;

export const DropdownContent = styled.div<{ $maxHeight?: string }>`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    flex: 1;
    overflow-y: auto;
    ${(props) =>
        props.$maxHeight &&
        css`
            max-height: ${props.$maxHeight};
        `}
`;

export const DropdownList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 6px;
`;

export const DropdownDivider = styled.div`
    height: 1px;
    background: #f3f4f6;
    margin: 4px 6px;
`;

export const DropdownFooter = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-top: 1px solid #f3f4f6;
    background: #fafafa;
`;

export const DropdownListItem = styled.button<{ $active?: boolean; $danger?: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
    padding: 8px 10px;
    border: 0;
    border-radius: 6px;
    background: ${(props) => (props.$active ? '#fff7ed' : 'transparent')};
    color: ${(props) => (props.$danger ? '#dc2626' : props.$active ? '#e8650a' : '#263238')};
    font-family: inherit;
    font-size: 13px;
    font-weight: ${(props) => (props.$active ? 600 : 400)};
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    transition:
        background 0.12s ease,
        color 0.12s ease;
    user-select: none;

    &:hover {
        background: ${(props) => (props.$active ? '#ffedd5' : '#f3f4f6')};
        color: ${(props) => (props.$danger ? '#b91c1c' : props.$active ? '#e8650a' : '#263238')};
        text-decoration: none;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

export const DropdownItemIcon = styled.span<{ $size?: DropdownItemIconSize }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${(props) => (props.$size === 'large' ? '26px' : '20px')};
    height: ${(props) => (props.$size === 'large' ? '26px' : '20px')};
    flex: 0 0 ${(props) => (props.$size === 'large' ? '26px' : '20px')};
    color: currentColor;

    svg {
        width: ${(props) => (props.$size === 'large' ? '26px' : '18px')};
        height: ${(props) => (props.$size === 'large' ? '26px' : '18px')};
        fill: currentColor;
    }
`;

export const DropdownItemImageIcon = styled.img<{ $size?: DropdownItemIconSize }>`
    width: ${(props) => (props.$size === 'large' ? '26px' : '24px')};
    height: ${(props) => (props.$size === 'large' ? '26px' : '24px')};
    flex: 0 0 ${(props) => (props.$size === 'large' ? '26px' : '24px')};
    border-radius: 4px;
    object-fit: cover;
`;

export const DropdownItemBody = styled.span`
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
`;

export const DropdownItemText = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    color: inherit;
    font-size: 13px;
    line-height: 18px;
`;

export const DropdownItemCaption = styled.span`
    color: #6b7280;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
`;

export const DropdownItemMeta = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    flex: 0 0 auto;
`;

export const DropdownBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 18px;
    padding: 0 6px;
    border-radius: 999px;
    background: #eef2ff;
    color: #4338ca;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
`;

export const DropdownFooterLink = styled.a`
    color: #455a64;
    font-size: 12px;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

export const DropdownSkeletonBlock = styled(SkeletonBlock).attrs({ as: 'span' })<{ $width: string; $height: string; $radius?: string }>`
    border-radius: ${(props) => props.$radius ?? '5px'};
`;

const DropdownSkeletonFooter = styled(DropdownFooter)<{ $justify?: string }>`
    justify-content: ${(props) => props.$justify ?? 'flex-start'};
    gap: 10px;
`;

const getDefaultSkeletonSections = (itemCount: number): DropdownSkeletonItemConfig[][] => [
    Array.from({ length: itemCount }, (_, index) => ({
        textWidth: index === 0 ? '52%' : '44%',
        captionWidth: index === 0 ? '76%' : '68%'
    }))
];

export const DropdownSkeletonListItem: FC<DropdownSkeletonItemConfig & { iconSize?: DropdownItemIconSize }> = ({
    textWidth = '44%',
    captionWidth = '68%',
    icon = true,
    iconSize = 'small'
}) => (
    <DropdownListItem as='div'>
        {icon && (
            <DropdownItemIcon $size={iconSize}>
                <DropdownSkeletonBlock $width={iconSize === 'large' ? '26px' : '20px'} $height={iconSize === 'large' ? '26px' : '20px'} $radius='50%' />
            </DropdownItemIcon>
        )}
        <DropdownItemBody>
            <DropdownSkeletonBlock $width={textWidth} $height='14px' />
            {captionWidth && <DropdownSkeletonBlock $width={captionWidth} $height='11px' />}
        </DropdownItemBody>
    </DropdownListItem>
);

export const DropdownSkeleton: FC<DropdownSkeletonProps> = ({ itemCount = 4, sections, iconSize = 'small', footerWidths, footerJustify }) => {
    const resolvedSections = sections ?? getDefaultSkeletonSections(itemCount);

    return (
        <>
            <DropdownContent aria-hidden='true'>
                <DropdownList>
                    {resolvedSections.map((section, sectionIndex) => (
                        <Fragment key={sectionIndex}>
                            {section.map((item, itemIndex) => (
                                <DropdownSkeletonListItem key={`${sectionIndex}-${itemIndex}`} {...item} iconSize={iconSize} />
                            ))}
                            {sectionIndex < resolvedSections.length - 1 && <DropdownDivider />}
                        </Fragment>
                    ))}
                </DropdownList>
            </DropdownContent>
            {footerWidths && footerWidths.length > 0 && (
                <DropdownSkeletonFooter aria-hidden='true' $justify={footerJustify}>
                    {footerWidths.map((width, index) => (
                        <DropdownSkeletonBlock key={index} $width={width} $height='12px' />
                    ))}
                </DropdownSkeletonFooter>
            )}
        </>
    );
};
